import { useMemo } from "react";

import { TagTextAlert } from "@bridgesplit/ui";
import { getLoanCollateralMarketUsdValue, getZcLedger, useOraclePrices } from "@bridgesplit/abf-react";
import { TransactionTypes } from "app/types";
import { COPY } from "app/constants";
import { getFeatureFlag, snakeCaseToTitleCase, SPACE } from "@bridgesplit/utils";

import { ActionProps } from "./types";
import { AppButton, BalanceDisplay } from "../../common";
import { TokenInputWithPrice } from "../../common/util";
import { LoanPositionStats } from "./LoanPositionStats";
import { useAddCollateral } from "./payments/hooks";

export default function TopUp({ loanExpanded, loopPosition }: ActionProps) {
    const {
        inputAmount,
        setInputAmount,
        collateral,
        collateralMetadata,
        supportedCollateralTokens,
        setCollateralToken,
        balance,
        insufficientBalance,
        balanceLoading,
        permissionDenied,
        permissionMessage,
        submit
    } = useAddCollateral({ loanExpanded });
    const { getOracle } = useOraclePrices([collateralMetadata?.mint]);

    const currentDebt = getZcLedger(loanExpanded)?.ledgerDebt.total ?? 0;
    const currentCollateral = collateral?.amount ?? 0;
    const newCollateral = currentCollateral + (inputAmount ?? 0);
    const selectedCollateralUsdPrice = collateral?.usdPrice ?? getOracle(collateralMetadata?.mint)?.usdPrice ?? 0;
    const currentCollateralValueUsd = getLoanCollateralMarketUsdValue(loanExpanded);
    const newCollateralValueUsd = currentCollateralValueUsd + (inputAmount ?? 0) * selectedCollateralUsdPrice;
    const canSelectCollateral = getFeatureFlag("multiCollateral");

    const ctaError = useMemo(() => {
        if (balanceLoading) return "Loading";
        if (insufficientBalance) return "Insufficient balance";
        if (!inputAmount) return "Enter an amount";
        return undefined;
    }, [balanceLoading, insufficientBalance, inputAmount]);

    return (
        <>
            <TokenInputWithPrice
                value={inputAmount}
                metadata={collateralMetadata}
                selectToken={
                    canSelectCollateral && supportedCollateralTokens && supportedCollateralTokens.length > 1
                        ? {
                              tokens: supportedCollateralTokens,
                              setToken: setCollateralToken,
                              header: "Select collateral",
                              showBalances: true
                          }
                        : null
                }
                supportedDepositTokens={canSelectCollateral ? supportedCollateralTokens : undefined}
                label="Amount"
                setValue={setInputAmount}
                hideUsdPrice={false}
                disabled={permissionDenied}
                belowInput={
                    <BalanceDisplay
                        isLoading={balanceLoading}
                        balance={balance}
                        metadata={collateralMetadata}
                        setMax={permissionDenied ? undefined : () => setInputAmount(balance)}
                        insufficientBalance={insufficientBalance}
                    />
                }
            />
            <LoanPositionStats
                loopPosition={loopPosition}
                loanExpanded={loanExpanded}
                newPrincipalAmount={currentDebt}
                newCollateralAmount={newCollateral}
                newCollateralValueUsd={newCollateralValueUsd}
                isFullRepay={false}
                loading={balanceLoading}
                show={{
                    debtAndCollateral: true,
                    health: true, //!!inputAmount,
                    netApyCollapsable: false,
                    executionDetails: false
                }}
            />
            {permissionDenied && (
                <TagTextAlert color="warning" textSx={{ display: "inline-block" }} icon="warning">
                    {permissionMessage}
                </TagTextAlert>
            )}
            <AppButton
                transactionType={TransactionTypes.Topup}
                isTransaction
                disabled={!!ctaError || permissionDenied}
                asyncCta={{ onClickWithResult: submit }}
                fullWidth
            >
                {ctaError ?? snakeCaseToTitleCase(COPY.TOP_UP_COLLATERAL_TERM, SPACE)}
            </AppButton>
        </>
    );
}
